import { Box, VStack } from "@chakra-ui/react";
import { Configure, InstantSearch } from "react-instantsearch";
import type { SearchClient } from "algoliasearch";
import type { AdCampaignSortOption } from "./utils";
import { sortToIndexSuffix } from "./utils";
import CampaignsTableHeader from "./CampaignsTableHeader";
import CampaignsTableHits from "./CampaignsTableHits";

export default function CampaignsTableAlgolia({
  searchClient,
  indexName,
  title,
  sortOrder,
  onSortChange,
  numericFilters,
  facetFilters,
  showPerformanceIndicator = true,
  showLegend,
}: {
  searchClient: SearchClient;
  indexName: string;
  /** Section title (e.g. "Active Campaigns", "Past Campaigns"). */
  title: string;
  sortOrder: AdCampaignSortOption;
  onSortChange: (v: AdCampaignSortOption) => void;
  /** Optional numeric filters (e.g. active vs past by schedule_end_at_unix). */
  numericFilters?: (string | string[])[];
  /** Optional facet filters (e.g. status:ACTIVE). */
  facetFilters?: (string | string[])[];
  /** When false, the status/performance dot is hidden (e.g. for Past Campaigns). */
  showPerformanceIndicator?: boolean;
  /** When false, the performance legend is hidden (e.g. for Past Campaigns). */
  showLegend?: boolean;
}) {
  const suffix = sortToIndexSuffix(sortOrder);
  const activeIndex = suffix ? `${indexName}_${suffix}` : indexName;
  const hasNumericFilters = numericFilters != null && numericFilters.length > 0;
  const hasFacetFilters = facetFilters != null && facetFilters.length > 0;

  return (
    <InstantSearch searchClient={searchClient} indexName={activeIndex}>
      {hasNumericFilters || hasFacetFilters ? (
        <Configure
          numericFilters={hasNumericFilters ? numericFilters : undefined}
          facetFilters={hasFacetFilters ? facetFilters : undefined}
        />
      ) : null}
      <VStack align="stretch" spacing={4}>
        <CampaignsTableHeader
          title={title}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
          showLegend={showLegend}
        />
        <Box
          overflowX="auto"
          borderWidth="1px"
          borderRadius="3xl"
          borderColor="gray.200"
          bg="white"
          boxShadow="sm"
        >
          <CampaignsTableHits
            showPerformanceIndicator={showPerformanceIndicator}
            isPastCampaigns={showLegend === false}
          />
        </Box>
      </VStack>
    </InstantSearch>
  );
}
